import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Progress,
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
} from 'reactstrap';
import { useAppDispatch } from '../../hooks';
import { documentActions } from '../../redux/actions';
import documentService from '../../services/document.service';
import styled from 'styled-components';
import DocumentTrash from './DocumentTrash';

const MainLayout = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const LeftPanel = styled.div`
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 200px);
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 5px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
`;

const FolderCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
  background: linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%);
  color: #1976D2;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transform: rotate(45deg);
    transition: all 0.5s;
  }

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 8px 20px rgba(33, 150, 243, 0.3);
    border-color: #2196F3;

    &:before {
      left: 100%;
    }
  }
`;

const FileCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid #e0e0e0;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 123, 255, 0.1),
      transparent
    );
    transition: all 0.5s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    border-color: #007bff;

    &:before {
      left: 100%;
    }
  }
`;

const FileIcon = styled.div<{ isFolder?: boolean }>`
  font-size: 64px;
  text-align: center;
  padding: 20px 0;
  color: ${props => props.isFolder ? '#1976D2' : '#007bff'};
  filter: ${props => props.isFolder ? 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.2))' : 'none'};
`;

const FileName = styled.div<{ isFolder?: boolean }>`
  font-weight: 600;
  text-align: center;
  padding: 10px;
  word-break: break-word;
  font-size: 0.9rem;
  color: ${props => props.isFolder ? '#1565C0' : '#333'};
`;

const FileInfo = styled.div<{ isFolder?: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.isFolder ? '#1976D2' : '#6c757d'};
  text-align: center;
  padding: 0 10px 10px;
`;

const ListViewRow = styled.div<{ isFolder?: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  transition: all 0.3s;
  background: ${props => props.isFolder 
    ? 'linear-gradient(90deg, rgba(33, 150, 243, 0.1) 0%, transparent 100%)'
    : 'white'};
  border-left: ${props => props.isFolder ? '4px solid #2196F3' : '4px solid transparent'};

  &:hover {
    background: ${props => props.isFolder 
      ? 'linear-gradient(90deg, rgba(33, 150, 243, 0.15) 0%, transparent 100%)'
      : '#f8f9fa'};
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const DropZone = styled.div<{ isDragging: boolean }>`
  border: 3px dashed ${props => props.isDragging ? '#007bff' : '#ccc'};
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
  background: ${props => props.isDragging 
    ? 'linear-gradient(135deg, #e7f3ff 0%, #cfe9ff 100%)' 
    : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'};
  transition: all 0.3s;
  box-shadow: ${props => props.isDragging ? '0 4px 12px rgba(0, 123, 255, 0.2)' : 'none'};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    border-color: #007bff;
    background: linear-gradient(135deg, #e7f3ff 0%, #d4e9ff 100%);
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 20px;
`;

interface FileItem {
  id: number;
  name: string;
  type: 'file' | 'folder';
  size?: number;
  fileType?: string;
  createdBy: number;
  creator?: { id: number; name: string };
  createdAt: string;
  itemCount?: {
    folders: number;
    files: number;
    total: number;
  };
}

interface BreadcrumbPath {
  id: string | number;
  name: string;
}

const DocumentLibrary: React.FC = () => {
  const dispatch = useAppDispatch();
  const [currentFolderId, setCurrentFolderId] = useState<string | number>('root');
  const [folders, setFolders] = useState<FileItem[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbPath[]>([
    { id: 'root', name: 'Thư viện tài liệu' },
  ]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ folders: FileItem[]; files: FileItem[] } | null>(null);
  
  // Modals
  const [createFolderModal, setCreateFolderModal] = useState(false);
  const [renameModal, setRenameModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateAlert, setDuplicateAlert] = useState<any>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [docxHtml, setDocxHtml] = useState<string>('');
  
  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<FileItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 0; // Admin có role = 0, teacher có role = 1

  useEffect(() => {
    loadFolderContents(currentFolderId);
  }, [currentFolderId]);

  const loadFolderContents = async (folderId: string | number) => {
    try {
      const result = await documentService.getFolderContents(folderId);
      if (result.success) {
        setFolders(result.data.folders || []);
        setFiles(result.data.files || []);
      }
    } catch (error) {
      console.error('Error loading folder:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert('Vui lòng nhập tên thư mục');
      return;
    }

    try {
      const result = await documentService.createFolder({
        name: newFolderName,
        parentId: currentFolderId === 'root' ? undefined : Number(currentFolderId),
      });

      if (result.success) {
        setCreateFolderModal(false);
        setNewFolderName('');
        loadFolderContents(currentFolderId);
      }
    } catch (error: any) {
      alert(error || 'Lỗi khi tạo thư mục');
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.size > 300 * 1024 * 1024) {
      alert('Kích thước file không được vượt quá 300MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    if (currentFolderId !== 'root') {
      formData.append('folderId', currentFolderId.toString());
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await documentService.uploadFile(formData, (progressEvent: any) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percentCompleted);
      });

      if (result.success) {
        loadFolderContents(currentFolderId);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      if (error.response?.data?.duplicate) {
        setDuplicateAlert(error.response.data);
      } else {
        alert(error.response?.data?.message || error.message || 'Lỗi khi tải file lên');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleItemClick = (item: FileItem) => {
    if (item.type === 'folder') {
      setCurrentFolderId(item.id);
      setBreadcrumbs([...breadcrumbs, { id: item.id, name: item.name }]);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolderId(newBreadcrumbs[newBreadcrumbs.length - 1].id);
  };

  const handleRename = async () => {
    if (!selectedItem || !renameValue.trim()) return;

    try {
      const result = selectedItem.type === 'folder'
        ? await documentService.updateFolder(selectedItem.id, { name: renameValue })
        : await documentService.updateFile(selectedItem.id, { name: renameValue });

      if (result.success) {
        setRenameModal(false);
        setRenameValue('');
        loadFolderContents(currentFolderId);
      }
    } catch (error: any) {
      alert(error || 'Lỗi khi đổi tên');
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      const result = selectedItem.type === 'folder'
        ? await documentService.deleteFolder(selectedItem.id)
        : await documentService.deleteFile(selectedItem.id);

      if (result.success) {
        setDeleteConfirmModal(false);
        loadFolderContents(currentFolderId);
      }
    } catch (error: any) {
      alert(error || 'Lỗi khi xóa');
    }
  };

  const handleDownload = async (file: FileItem) => {
    try {
      const blob = await documentService.downloadFile(file.id);
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Lỗi khi tải file');
    }
  };

  const handlePreviewFile = async (file: FileItem) => {
    try {
      setPreviewFile(file);
      const blob = await documentService.downloadFile(file.id);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      
      // Handle DOCX files with mammoth.js
      if (file.fileType?.toLowerCase() === 'docx') {
        try {
          const mammoth = await import('mammoth');
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocxHtml(result.value);
        } catch (err) {
          console.error('Mammoth error:', err);
          setDocxHtml('');
        }
      } else {
        setDocxHtml('');
      }
      
      setPreviewModal(true);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Lỗi khi xem trước file');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewModal(false);
    setPreviewFile(null);
    setPreviewUrl('');
    setDocxHtml('');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const result: any = await dispatch(documentActions.searchDocuments(searchQuery));
      if (result.success) {
        setSearchResults(result.data);
      }
    } catch (error) {
      alert('Lỗi khi tìm kiếm');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (item: FileItem): string => {
    if (item.type === 'folder') return '📁';
    
    const ext = item.fileType?.toLowerCase();
    const iconMap: Record<string, string> = {
      pdf: '📕',
      doc: '📘',
      docx: '📘',
      txt: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📙',
      pptx: '📙',
      zip: '🗜️',
      rar: '🗜️',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🎨',
      gif: '🎞️',
      svg: '🎨',
      mp4: '🎬',
      avi: '🎬',
      mov: '🎬',
      mp3: '🎵',
      wav: '🎵',
      flac: '🎵',
    };
    
    return iconMap[ext || ''] || '📄';
  };

  const displayItems = searchQuery.trim()
    ? [...folders, ...files].filter(item =>
        item.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : [...folders, ...files];

  return (
    <Container fluid style={{ padding: '20px' }}>
      {/* Title + Toolbar in one row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px',
        borderBottom: '2px solid #dee2e6',
        paddingBottom: '15px',
      }}>
        <h2 style={{ margin: 0, whiteSpace: 'nowrap' }}>📚 Thư viện tài liệu</h2>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            color={showTrash ? 'secondary' : 'light'}
            onClick={() => setShowTrash(!showTrash)}
            style={showTrash ? { fontWeight: 700 } : {}}
          >
            🗑️ Thùng rác
          </Button>

          <div style={{ position: 'relative' }}>
            <Input
              type="text"
              placeholder="🔍 Tìm kiếm trong thư mục..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '260px', paddingRight: searchQuery ? '30px' : undefined }}
            />
            {searchQuery && (
              <span
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute', right: '10px', top: '50%',
                  transform: 'translateY(-50%)', cursor: 'pointer',
                  color: '#999', fontSize: '1rem', lineHeight: 1,
                }}
              >
                ✕
              </span>
            )}
          </div>
          <Button
            color="light"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          >
            {viewMode === 'grid' ? '📋 Danh sách' : '🔲 Lưới'}
          </Button>
        </div>
      </div>


      {/* Trash View */}
      {showTrash && (
        <DocumentTrash onBack={() => { setShowTrash(false); loadFolderContents(currentFolderId); }} />
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div style={{ marginBottom: '20px' }}>
          <Progress value={uploadProgress} />
          <div style={{ textAlign: 'center', marginTop: '5px' }}>
            Đang tải lên... {uploadProgress}%
          </div>
        </div>
      )}

      {/* Duplicate Alert */}
      {duplicateAlert && (
        <Alert color="warning" toggle={() => setDuplicateAlert(null)}>
          <strong>File đã tồn tại!</strong> File "{duplicateAlert.duplicate.name}" đã có trong hệ thống.
          <br />
          <strong>Vị trí:</strong> {duplicateAlert.duplicate.path}
          <br />
          <strong>Kích thước:</strong> {formatFileSize(duplicateAlert.duplicate.size)}
        </Alert>
      )}

      {/* Main Layout - 2 Columns */}
      <MainLayout style={{ display: showTrash ? 'none' : 'flex' }}>
        {/* Left Panel - Drop Zone & Quick Actions */}
        {!searchQuery.trim() && (
          <LeftPanel>
            {/* Action buttons above drop zone */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button color="primary" style={{ flex: 1 }} onClick={() => setCreateFolderModal(true)}>
                📁 Tạo thư mục
              </Button>
              <Button color="success" style={{ flex: 1 }} onClick={() => document.getElementById('fileInput')?.click()}>
                ⬆️ Tải file lên
              </Button>
              <input
                id="fileInput"
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => handleFileUpload(e.target.files)}
              />
            </div>
            <DropZone
              isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>📂</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '5px' }}>
                Kéo thả file vào đây
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#1976D2',
                fontWeight: 600,
                background: 'rgba(25,118,210,0.08)',
                borderRadius: '4px',
                padding: '3px 8px',
                marginBottom: '5px',
                wordBreak: 'break-all',
              }}>
                📁 {breadcrumbs.map(b => b.name).join(' / ')}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                hoặc nhấn "Tải file lên"
              </div>
              <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '8px' }}>
                (Tối đa 300MB)
              </div>
            </DropZone>
          </LeftPanel>
        )}

        {/* Right Panel - Files & Folders List */}
        <RightPanel>
          {/* Breadcrumb - just above file list */}
          <Breadcrumb style={{ margin: '0 0 10px 0', background: 'transparent', padding: 0 }}>
            {breadcrumbs.map((crumb, index) => {
              const isActive = index === breadcrumbs.length - 1;
              return (
                <BreadcrumbItem
                  key={crumb.id}
                  active={isActive}
                  onClick={() => !isActive && handleBreadcrumbClick(index)}
                  style={{
                    cursor: isActive ? 'default' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <span style={isActive ? {
                    fontWeight: 700,
                    color: '#1a1a1a',
                    background: '#e9ecef',
                    borderRadius: '5px',
                    padding: '2px 8px',
                    fontSize: '0.9rem',
                  } : {
                    color: '#1976D2',
                    fontSize: '0.9rem',
                  }}>
                    {isActive ? '📁 ' : ''}{crumb.name}
                  </span>
                </BreadcrumbItem>
              );
            })}
          </Breadcrumb>
          <ScrollableContent>
            {/* Files and Folders Display */}
            {viewMode === 'grid' ? (
              <FileGrid>
              {displayItems.map((item) => {
                const CardComponent = item.type === 'folder' ? FolderCard : FileCard;
                return (
                  <CardComponent key={`${item.type}-${item.id}`}>
                    <CardBody
                      onClick={() => {
                        if (item.type === 'folder') {
                          handleItemClick(item);
                        } else {
                          handlePreviewFile(item);
                        }
                      }}
                      style={{ padding: '0' }}
                    >
                      <FileIcon isFolder={item.type === 'folder'}>{getFileIcon(item)}</FileIcon>
                      <FileName isFolder={item.type === 'folder'}>{item.name}</FileName>
                      <FileInfo isFolder={item.type === 'folder'}>
                        {item.type === 'folder' ? (
                          <>
                            Thư mục
                            {item.itemCount && (
                              <>
                                <br />
                                📁 {item.itemCount.folders} thư mục • 📄 {item.itemCount.files} file
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {item.fileType?.toUpperCase()}
                            {item.size && ` • ${formatFileSize(item.size)}`}
                          </>
                        )}
                        <br />
                        {item.creator?.name}
                      </FileInfo>
                      <div style={{ 
                        padding: '10px', 
                        borderTop: item.type === 'folder' 
                          ? '1px solid rgba(25, 118, 210, 0.2)' 
                          : '1px solid #dee2e6',
                        background: item.type === 'folder' 
                          ? 'rgba(33, 150, 243, 0.1)' 
                          : 'transparent'
                      }}>
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                          {item.type === 'file' && (
                            <Button
                              size="sm"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(item);
                              }}
                            >
                              ⬇️
                            </Button>
                          )}
                          <Button
                            size="sm"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItem(item);
                              setRenameValue(item.name);
                              setRenameModal(true);
                            }}
                          >
                            ✏️
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItem(item);
                              setDeleteConfirmModal(true);
                            }}
                          >
                            🗑️
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </CardComponent>
                );
              })}
            </FileGrid>
          ) : (
            <div>
              {displayItems.map((item) => (
                <ListViewRow
                  key={`${item.type}-${item.id}`}
                  isFolder={item.type === 'folder'}
                  onClick={() => {
                    if (item.type === 'folder') {
                      handleItemClick(item);
                    } else {
                      handlePreviewFile(item);
                    }
                  }}
                >
                  <div style={{ flex: '0 0 50px', fontSize: '32px' }}>{getFileIcon(item)}</div>
                  <div style={{ flex: '1' }}>
                    <div style={{ 
                      fontWeight: item.type === 'folder' ? 700 : 500,
                      color: item.type === 'folder' ? '#1976D2' : '#333'
                    }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                      {item.creator?.name} • {new Date(item.createdAt).toLocaleDateString('vi-VN')}
                      {item.type === 'folder' && item.itemCount && (
                        <span style={{ marginLeft: '10px', color: '#1976D2', fontWeight: 500 }}>
                          • 📁 {item.itemCount.folders} • 📄 {item.itemCount.files}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ flex: '0 0 120px', textAlign: 'right', color: '#6c757d' }}>
                    {item.type === 'file' && item.size ? formatFileSize(item.size) : '-'}
                  </div>
                  <div style={{ flex: '0 0 200px', textAlign: 'right' }}>
                    {item.type === 'file' && (
                      <Button size="sm" color="info" onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(item);
                      }}>
                        ⬇️ Tải xuống
                      </Button>
                    )}{' '}
                    <Button
                      size="sm"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem(item);
                        setRenameValue(item.name);
                        setRenameModal(true);
                      }}
                    >
                      ✏️
                    </Button>{' '}
                    <Button
                      size="sm"
                      color="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem(item);
                        setDeleteConfirmModal(true);
                      }}
                    >
                      🗑️
                    </Button>
                  </div>
                </ListViewRow>
              ))}
            </div>
            )}

            {displayItems.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                {searchQuery.trim() ? `Không tìm thấy kết quả cho "${searchQuery}"` : 'Thư mục trống'}
              </div>
            )}
          </ScrollableContent>
        </RightPanel>
      </MainLayout>      {/* Create Folder Modal */}
      <Modal isOpen={createFolderModal} toggle={() => setCreateFolderModal(false)}>
        <ModalHeader toggle={() => setCreateFolderModal(false)}>Tạo thư mục mới</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Tên thư mục</Label>
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nhập tên thư mục..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCreateFolder}>
            Tạo
          </Button>
          <Button color="secondary" onClick={() => setCreateFolderModal(false)}>
            Hủy
          </Button>
        </ModalFooter>
      </Modal>

      {/* Rename Modal */}
      <Modal isOpen={renameModal} toggle={() => setRenameModal(false)}>
        <ModalHeader toggle={() => setRenameModal(false)}>Đổi tên</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Tên mới</Label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Nhập tên mới..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleRename}>
            Lưu
          </Button>
          <Button color="secondary" onClick={() => setRenameModal(false)}>
            Hủy
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModal} toggle={() => setDeleteConfirmModal(false)}>
        <ModalHeader toggle={() => setDeleteConfirmModal(false)}>Xác nhận xóa</ModalHeader>
        <ModalBody>
          Bạn có chắc chắn muốn xóa "{selectedItem?.name}"?
          {selectedItem?.type === 'folder' && (
            <div style={{ color: '#856404', background: '#fff3cd', padding: '8px 12px', borderRadius: '6px', marginTop: '10px' }}>
              ⚠️ Toàn bộ file và thư mục con cũng sẽ bị chuyển vào thùng rác!
            </div>
          )}
          <div style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '10px' }}>
            💡 File sẽ được chuyển vào Thùng rác. Bạn có thể khôi phục sau.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>
            Chuyển vào thùng rác
          </Button>
          <Button color="secondary" onClick={() => setDeleteConfirmModal(false)}>
            Hủy
          </Button>
        </ModalFooter>
      </Modal>

      {/* File Preview Modal */}
      <Modal 
        isOpen={previewModal} 
        toggle={closePreview}
        size="xl"
        style={{ maxWidth: '90vw' }}
      >
        <ModalHeader toggle={closePreview}>
          {previewFile?.name}
        </ModalHeader>
        <ModalBody style={{ minHeight: '70vh', maxHeight: '80vh', overflow: 'auto' }}>
          {previewFile && (
            <>
              {/* PDF Files */}
              {previewFile.fileType?.toLowerCase() === 'pdf' && (
                <iframe
                  src={previewUrl}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                  title={previewFile.name}
                />
              )}

              {/* Image Files */}
              {['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(previewFile.fileType?.toLowerCase() || '') && (
                <div style={{ textAlign: 'center' }}>
                  <img 
                    src={previewUrl} 
                    alt={previewFile.name}
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* Text Files */}
              {['txt', 'md', 'json', 'xml', 'csv', 'log'].includes(previewFile.fileType?.toLowerCase() || '') && (
                <iframe
                  src={previewUrl}
                  style={{ width: '100%', height: '70vh', border: '1px solid #dee2e6', borderRadius: '4px' }}
                  title={previewFile.name}
                />
              )}

              {/* DOCX Files - Use mammoth.js */}
              {previewFile.fileType?.toLowerCase() === 'docx' && (
                <div style={{ 
                  padding: '40px', 
                  background: 'white', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '4px',
                  maxHeight: '70vh',
                  overflow: 'auto',
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '1.6'
                }}>
                  {docxHtml ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: docxHtml }} 
                      style={{
                        fontSize: '14px',
                        color: '#333'
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Đang tải...</span>
                      </div>
                      <p style={{ marginTop: '15px' }}>Đang tải nội dung DOCX...</p>
                    </div>
                  )}
                </div>
              )}

              {/* Other Office Documents - DOC, XLS, PPT (not supported for preview) */}
              {['doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(previewFile.fileType?.toLowerCase() || '') && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📄</div>
                  <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '10px' }}>
                    File {previewFile.fileType?.toUpperCase()}
                  </p>
                  <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                    Trình duyệt không hỗ trợ xem trước trực tiếp file loại này.
                  </p>
                  <Button color="primary" onClick={() => handleDownload(previewFile)} size="lg">
                    ⬇️ Tải xuống để xem
                  </Button>
                </div>
              )}

              {/* Video Files */}
              {['mp4', 'webm', 'ogg', 'mov'].includes(previewFile.fileType?.toLowerCase() || '') && (
                <video 
                  controls 
                  style={{ width: '100%', maxHeight: '70vh' }}
                  src={previewUrl}
                >
                  Trình duyệt không hỗ trợ video.
                </video>
              )}

              {/* Audio Files */}
              {['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(previewFile.fileType?.toLowerCase() || '') && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <audio controls style={{ width: '100%' }} src={previewUrl}>
                    Trình duyệt không hỗ trợ audio.
                  </audio>
                </div>
              )}

              {/* Unsupported File Types */}
              {!['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 
                  'txt', 'md', 'json', 'xml', 'csv', 'log',
                  'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt',
                  'mp4', 'webm', 'ogg', 'mov',
                  'mp3', 'wav', 'aac', 'm4a'].includes(previewFile.fileType?.toLowerCase() || '') && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📄</div>
                  <p>Không thể xem trước file loại này.</p>
                  <p style={{ color: '#6c757d', marginTop: '10px' }}>
                    File type: {previewFile.fileType?.toUpperCase()}
                  </p>
                  <Button color="primary" onClick={() => handleDownload(previewFile)} style={{ marginTop: '20px' }}>
                    ⬇️ Tải xuống để xem
                  </Button>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => previewFile && handleDownload(previewFile)}>
            ⬇️ Tải xuống
          </Button>
          <Button color="secondary" onClick={closePreview}>
            Đóng
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default DocumentLibrary;
